
import apiConfig from '@/config/api.config'
import ImageUploader from '@/components/common/ImageUploader.vue'
import MultiImageUploader from '@/components/common/MultiImageUploader.vue'
import { applyImageDelta } from '@/services/productImageApi'
import ExportButton from '@/components/common/ExportButton.vue'
import ImportButton from '@/components/common/ImportButton.vue'
import BarcodeDisplay from '@/components/common/BarcodeDisplay.vue'
import ProductBarcodeLabel from '@/components/common/ProductBarcodeLabel.vue'
import { SPEC_FIELD_MAP } from '@/utils/specFieldMap'
import { resolveProductImageUrls, resolveProductImageUrl } from '@/utils/image'
import { getCategoriesForSelector, getCategoryName, fetchCategoryDictionary, CATEGORY_DICTIONARY } from '@/utils/category-dictionary'
import { fetchPublicConfig } from '@/utils/publicConfig'
// 对话原语（原生弹窗治理 Phase 1）：本文件 33 处原生弹窗（28 alert + 5 confirm）全部迁到这里。
// 分类口径见 docs/NATIVE_DIALOG_AUDIT.md §3.1（先判 C，再判 D，再判 B，兜底 A）与本文件逐处注释。
import { showMessage, confirmAction, confirmDanger } from '@/utils/dialog'

export default {
  name: 'ProductManage',
  components: {
    ImageUploader,
    MultiImageUploader,
    ExportButton,
    ImportButton,
    BarcodeDisplay,
    ProductBarcodeLabel
  },
  data () {
    return {
      products: [],
      selectedProducts: [],
      selectAll: false,
      showTierBadge: true, // "惠/靓/正"角标全局开关（public config 下发；默认显示）——管理列表预览同受控
      batchMode: false,
      showAddModal: false,
      showLabelModal: false,
      labelProducts: [],
      editingProduct: null,
      frameOptions: [], // active 商品边框选项（编辑弹窗 radio 缩略预览；边框系统一期）
      searchKeyword: '',
      statusFilter: 'active', // 默认显示上架中商品
      searchTimer: null,
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      // 打开编辑弹窗时的主图快照（保存时差异对比走专用 API，见 saveProduct；非表单字段）
      initialMainImages: [],
      form: {
        name: '',
        description: '',
        price: '',
        original_price: '',
        stock: '',
        category: 'tire',
        // 商品主图（最多6张，1:1比例）
        main_images: [],
        // 商品详情图（最多10张）
        detail_images: [],
        // 营销展示图（首页/搜索卡片），单张可选
        marketing_image: '',
        brand: '',
        specification: '',
        model: '',
        tier: '正',
        sales_count: 0,
        weight: 100,
        inner_code: '',
        barcode: '',
        unit: '个',
        units: [{ unit_name: '个', ratio: 1, is_base: 1 }],
        pack_unit: '',
        pack_quantity: 1,
        repair_difficulty: 3,
        admin_override_rating: 0,
        admin_override_enabled: false,
        // 新字段
        applicable_models: [],
        product_specs: {},
        search_tags: [],
        search_terms: []
      },
      // 规格分类 → 字段映射（唯一真理源 @/utils/specFieldMap，PC/搜索/移动端三处共用）
      specCategoryMap: SPEC_FIELD_MAP,
      // 分类选择器选项（初始=内置字典；created 拉取 /categories 后换 API active 品类）
      categories: getCategoriesForSelector(),
      showImportPreview: false,
      importData: [],
      importErrors: [],
      // 标签管理
      availableModels: [],
      modelKeyword: '',
      specOptions: {},
      searchTagsInput: '',
      newSearchTerm: { term: '', term_type: 'alias', weight: 5 }
    }
  },
  computed: {
    exportGroups () {
      return [
        { label: '商品', templates: [
          { value: 'product-master', label: '商品基础', desc: '含规格/车型/标签（展平）' },
          { value: 'product-operation', label: '商品运营', desc: '维修难度/评分/推荐' },
          { value: 'product-price', label: '商品价格', desc: '售价/原价' }
        ]}
      ]
    },
    importTemplates () {
      return [
        { value: 'product-master', label: '商品基础', desc: 'inner_code 去重 upsert' },
        { value: 'product-operation', label: '商品运营', desc: '维修难度/推荐/赠品标记' },
        { value: 'product-price', label: '商品价格', desc: '售价/原价更新' }
      ]
    },
    // 适用车型搜索过滤：匹配关键词；已选车型始终保留，搜索不丢已选
    filteredModels () {
      const kw = (this.modelKeyword || '').trim().toLowerCase()
      if (!kw) return this.availableModels
      return this.availableModels.filter(m => {
        const matched = (m.tag_label || '').toLowerCase().includes(kw) || (m.tag_value || '').toLowerCase().includes(kw)
        return matched || this.isModelSelected(m.tag_value)
      })
    },
    isSuperAdmin () { return this.$store.getters.isSuperAdmin },
    validImportCount () {
      return this.importData.filter(item => !item.errors || item.errors.length === 0).length
    },
    totalPages () {
      return Math.ceil(this.pagination.total / this.pagination.limit) || 1
    },
    currentSpecFields () {
      return this.specCategoryMap[this.form.category] || []
    },
    // 基础单位名（单位管理区换算提示用）
    baseUnitName () {
      const b = (this.form.units || []).find(u => u.is_base)
      return b ? (b.unit_name || '').trim() : ''
    }
  },
  methods: {
    // 单位层级元信息（2026-09-19 单位层级收口）：层级=按换算比（相对基础单位绝对值）升序的秩，
    // 与保存后后端 normalizeInput 的排序一致——绝不按行序/单位名/字段空否推导。
    // 返回 null=数据不全（刚加的空行）；双换算显示相对上一级仅整除时给出（1件=10盒·200个）。
    unitLevelMeta (u) {
      if (!u || u.is_base || !u.unit_name || !(Number(u.ratio) > 1)) return null
      const valid = (this.form.units || []).filter(x => x.unit_name && (x.is_base || Number(x.ratio) > 1))
      const sorted = valid.slice().sort((a, b) => (a.is_base === b.is_base ? Number(a.ratio) - Number(b.ratio) : (a.is_base ? -1 : 1)))
      const idx = sorted.indexOf(u)
      if (idx <= 0) return null
      const tag = idx === 1 ? '中包装' : (idx === 2 ? '外包装' : `第${idx + 1}级`)
      const prev = sorted[idx - 1]
      const r = Number(u.ratio)
      const pr = Number(prev.ratio) || 1
      const rel = (pr > 1 && r > pr && r % pr === 0) ? (r / pr) : null
      const dual = `1${u.unit_name}=${rel ? `${rel}${prev.unit_name}·` : ''}${r}${this.baseUnitName}`
      return { tag, dual }
    },
    handleSearch () {
      this.pagination.page = 1
      this.fetchProducts()
    },
    openLabelPrint () {
      if (this.selectedProducts.length === 0) return
      // A 类（校验提示）：超出单次上限，需要用户减少选择 → warning，不弹窗
      if (this.selectedProducts.length > 100) {
        showMessage('单次最多打印 100 个商品标签', { type: 'warning' })
        return
      }
      // 获取选中商品的完整信息（从 products 列表中查找）
      const selectedIds = new Set(this.selectedProducts)
      this.labelProducts = this.products.filter(p => selectedIds.has(p.id))
      this.showLabelModal = true
    },
    debounceSearch () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.pagination.page = 1
        this.fetchProducts()
      }, 300)
    },
    async fetchProducts () {
      try {
        const token = localStorage.getItem('token')
        const res = await this.axios.get(apiConfig.apiList.getAdminProducts, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            keyword: this.searchKeyword || undefined,
            status: this.statusFilter !== 'all' ? this.statusFilter : undefined,
            page: this.pagination.page,
            limit: this.pagination.limit
          }
        })
        if (res.data.code === 200) {
          this.products = res.data.data
          this.pagination.total = res.data.pagination?.total || 0
        }
      } catch (err) {
        console.error('获取商品失败:', err)
      }
    },
    goToPage (page) {
      if (page < 1 || page > this.totalPages || page === this.pagination.page) return
      this.pagination.page = page
      this.fetchProducts()
    },
    changePageSize (limit) {
      this.pagination.limit = parseInt(limit)
      this.pagination.page = 1
      this.fetchProducts()
    },
    categoryText (category) {
      return getCategoryName(category)
    },
    // 商品缩略图（主图 = 正式排序第 1 张；统一 helper 归一，禁止页面自 parse）
    productThumb (product) {
      return resolveProductImageUrl(product)
    },
    async editProduct (product) {
      this.editingProduct = product
      // 确保标签数据已加载
      await this.fetchTags()
      // 主图初始化 = 服务端现状归一（2026-09-19 P0 收口）：统一 helper（images 数组优先，
      // 空 fallback [image] 存量单图形态），禁止页面自行 JSON.parse / image 三元
      const mainImages = resolveProductImageUrls(product)
      // 解析详情图字段
      let detailImages = product.detail_images
      if (detailImages && typeof detailImages === 'string') {
        try {
          detailImages = JSON.parse(detailImages)
        } catch (e) {
          detailImages = []
        }
      }
      // 解析新字段
      let applicableModels = product.applicable_models
      if (applicableModels && typeof applicableModels === 'string') {
        try { applicableModels = JSON.parse(applicableModels) } catch (e) { applicableModels = [] }
      }
      let productSpecs = product.product_specs
      if (productSpecs && typeof productSpecs === 'string') {
        try { productSpecs = JSON.parse(productSpecs) } catch (e) { productSpecs = {} }
      }
      let searchTags = product.search_tags
      if (searchTags && typeof searchTags === 'string') {
        try { searchTags = JSON.parse(searchTags) } catch (e) { searchTags = [] }
      }
      this.searchTagsInput = (searchTags || []).join(', ')
      this.form = {
        ...product,
        // frame_id/frame_mode 显式占位：Vue2 不追踪后加属性，缺键会让边框回填赋值不触发视图更新
        frame_id: '',
        frame_mode: 'inherit',
        repair_difficulty: product.repair_difficulty || 3,
        admin_override_rating: product.admin_override_rating || 0,
        admin_override_enabled: !!product.admin_override_enabled,
        main_images: mainImages,
        detail_images: Array.isArray(detailImages) ? detailImages : [],
        marketing_image: product.marketing_image || '',
        applicable_models: applicableModels || [],
        product_specs: productSpecs || {},
        search_tags: searchTags || [],
        search_terms: []
      }
      // 图片初始快照（保存时与终态做差异对比，差异走专用 API——不随整对象 PUT 覆盖）
      this.initialMainImages = [...this.form.main_images]
      // 规范化单位列表：后端 units 优先，空则从旧字段（unit/pack_unit）构造
      const pu = product.units
      if (Array.isArray(pu) && pu.length) {
        this.form.units = pu.map(u => ({ unit_name: (u.unit_name || u.name || '').trim(), ratio: u.is_base ? 1 : (Number(u.ratio != null ? u.ratio : u.ratio_to_base) || 1), is_base: u.is_base ? 1 : 0 }))
      } else {
        const list = [{ unit_name: product.unit || '个', ratio: 1, is_base: 1 }]
        if (product.pack_unit && Number(product.pack_quantity) > 1) list.push({ unit_name: product.pack_unit, ratio: Number(product.pack_quantity), is_base: 0 })
        this.form.units = list
      }
      // 加载该商品的搜索词（仅编辑态）
      await this.fetchSearchTerms(product.id)
      // 边框选择器（边框系统二期三态）：active 边框选项 + 本商品当前态（mode + 绑定），二者独立容错
      this.form.frame_id = ''
      this.form.frame_mode = 'inherit'
      this.fetchFrameOptions()
      this.axios.get(`/admin/products/${product.id}/frame`).then(res => {
        if (res.data.code === 200 && res.data.data && res.data.data.mode) {
          this.form.frame_mode = res.data.data.mode
          this.form.frame_id = res.data.data.frame ? res.data.data.frame.id : ''
        }
      }).catch(() => {})
      // 选择器选项对齐 API active 品类；本商品若属停用/未知品类，追加旧值项（保存仍写原 slug）
      this.syncCategoryOptions()
    },
    // active 边框精简选项（id/name/asset_url），编辑弹窗 radio 缩略预览用
    fetchFrameOptions () {
      this.axios.get('/admin/product-frames/options').then(res => {
        if (res.data.code === 200) this.frameOptions = res.data.data || []
      }).catch(() => {})
    },
    closeModal () {
      this.showAddModal = false
      this.editingProduct = null
      this.resetForm()
      // 关闭弹窗清掉「（已停用）」旧值追加项（resetForm 后 form.category='tire'，active 正常项）
      this.syncCategoryOptions()
    },
    // 分类选择器选项对齐（品类数据化第一阶段）：API active 品类为主源；编辑商品当前品类
    // 不在 active（停用/未知）时追加「xxx（已停用）」保值项——不清空、保存不改写原 slug
    syncCategoryOptions () {
      const opts = getCategoriesForSelector().map(o => ({ ...o }))
      const cur = this.form.category
      if (cur && !opts.some(o => o.value === cur)) {
        opts.push({ value: cur, label: getCategoryName(cur) + '（已停用）' })
      }
      this.categories = opts
    },
    resetForm () {
      this.form = {
        name: '',
        description: '',
        price: '',
        original_price: '',
        stock: '',
        category: 'tire',
        main_images: [],
        detail_images: [],
        marketing_image: '',
        brand: '',
        specification: '',
        model: '',
        tier: '正',
        sales_count: 0,
        weight: 100,
        inner_code: '',
        barcode: '',
        unit: '个',
        units: [{ unit_name: '个', ratio: 1, is_base: 1 }],
        pack_unit: '',
        pack_quantity: 1,
        repair_difficulty: 3,
        admin_override_rating: 0,
        admin_override_enabled: false,
        applicable_models: [],
        product_specs: {},
        search_tags: [],
        search_terms: [],
        frame_id: '',
        frame_mode: 'inherit'
      }
      this.searchTagsInput = ''
      this.newSearchTerm = { term: '', term_type: 'alias', weight: 5 }
    },
    async saveProduct () {
      try {
        const token = localStorage.getItem('token')
        // 构建提交数据，确保字段名与后端匹配
        const submitData = {
          ...this.form,
          marketing_image: this.form.marketing_image || '',
          // 单位配置（后端 saveUnits 双写旧 unit/pack_unit 字段，保证兼容）
          units: this.form.units.filter(u => u.unit_name && String(u.unit_name).trim()).map(u => ({
            name: String(u.unit_name).trim(), ratio: u.is_base ? 1 : (Number(u.ratio) || 1), is_base: u.is_base ? 1 : 0
          }))
        }
        delete submitData.main_images
        delete submitData.detail_images
        delete submitData.search_terms

        if (this.editingProduct) {
          // 图片管理收口（2026-09-19）：编辑保存不随整对象 PUT 覆盖 image/images（后端对缺省键
          // 已不触碰），图片差异走专用 API（删除→新增→终态排序），并发不再互相清图
          delete submitData.image
          delete submitData.images
          await this.axios.put(
            `${apiConfig.apiList.updateProduct}/${this.editingProduct.id}`,
            { ...submitData, status: this.editingProduct.status },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          // 「部分成功」三处（审计 §5.3 单独点名：主体成功、附属失败，不能机械换 Toast）：
          // 原实现每处各弹一个 alert，最后又弹「商品更新成功」——成功文案排在最后，把失败信息盖掉，
          // 用户会以为全成了。改为**只收集不立即提示**，收尾一次性告知：
          //   有部分失败 → 一条 warning（长时长够读完）；全部成功 → 一条 positive。
          // 审计建议的「Toast + 可展开详情 / inline 警告条」超出本段原语能力（不改 FormDialog/不新增组件），
          // 故以 warning + 8000ms 承载，列为交付报告遗留项。
          const partialFailures = []
          // 边框三态独立提交（bindings/opt-outs 两表；失败不回滚商品保存，仅提示）
          if (this.form.frame_mode === 'frame' && !this.form.frame_id) {
            partialFailures.push('边框未提交：单独设置需先选择一个边框（或改选跟随全局/不显示）')
          } else {
            try {
              await this.axios.put(
                `/admin/products/${this.editingProduct.id}/frame`,
                { mode: this.form.frame_mode, frame_id: this.form.frame_mode === 'frame' ? this.form.frame_id : null },
                { headers: { Authorization: `Bearer ${token}` } }
              )
            } catch (fErr) {
              console.error('边框绑定失败:', fErr)
              partialFailures.push('边框绑定失败：' + ((fErr.response && fErr.response.data && fErr.response.data.message) || '请稍后重试'))
            }
          }
          // 图片差异应用（专用 API；失败不回滚商品保存，明确提示）
          try {
            await applyImageDelta(this.editingProduct.id, this.initialMainImages, this.form.main_images)
          } catch (imgErr) {
            console.error('图片变更应用失败:', imgErr)
            partialFailures.push('图片变更未完全应用（可能与他人并发编辑冲突）：' + ((imgErr.response && imgErr.response.data && imgErr.response.data.message) || imgErr.message || '请刷新后重试'))
          }
          if (partialFailures.length) {
            showMessage('商品已保存，但以下内容未完全生效：' + partialFailures.join('；'), { type: 'warning', timeout: 8000 })
          } else {
            showMessage('商品更新成功', { type: 'positive' })
          }
        } else {
          // 新建商品：图片随 POST 一次落库（第一张即主图——后端强制 image=images[0]）
          submitData.image = (this.form.main_images && this.form.main_images[0]) || ''
          submitData.images = this.form.main_images
          await this.axios.post(
            apiConfig.apiList.createProduct,
            submitData,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          showMessage('商品创建成功', { type: 'positive' })
        }
        this.closeModal()
        this.fetchProducts()
      } catch (err) {
        console.error('保存商品失败:', err)
        showMessage('保存失败', { type: 'negative' })
      }
    },
    // 添加一个包装单位行
    addUnit () {
      this.form.units.push({ unit_name: '', ratio: '', is_base: 0 })
    },
    // 删除某个单位行（基础单位不可删）
    removeUnit (idx) {
      if (this.form.units[idx] && this.form.units[idx].is_base) return
      this.form.units.splice(idx, 1)
    },
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmDanger。
    // 归 **C**：DELETE /admin/products/:id 是 `DELETE FROM products WHERE id = ?`（服务端 admin.js:1753）
    // ——**物理删除、不可恢复**（审计 §3.3 C 类 (a)）。原文案只说「确定要删除这个商品吗？」偏弱，
    // 按门面签名要求补足 consequence。请求放进弹窗内：loading 防双击；失败留在弹窗里显示原因。
    async deleteProduct (id) {
      await confirmDanger({
        parent: this,
        title: '删除商品',
        message: '确定要删除这个商品吗？',
        consequence: '商品记录将被永久删除且不可恢复，请确认要删的是当前这个商品。',
        okLabel: '删除',
        action: async () => {
          const token = localStorage.getItem('token')
          await this.axios.delete(
            `${apiConfig.apiList.deleteProduct}/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          showMessage('商品已删除', { type: 'positive' })
          this.fetchProducts()
        }
      })
    },
    async toggleProductStatus (product) {
      const newStatus = product.status === 'active' ? 'inactive' : 'active'
      const action = newStatus === 'active' ? '上架' : '下架'
      try {
        const token = localStorage.getItem('token')
        // 发送完整的商品数据，确保后端能正确更新
        await this.axios.put(
          `${apiConfig.apiList.updateProduct}/${product.id}`,
          {
            name: product.name,
            description: product.description,
            price: product.price,
            original_price: product.original_price,
            category: product.category,
            stock: product.stock,
            status: newStatus,
            brand: product.brand,
            specification: product.specification,
            model: product.model,
            tier: product.tier,
            sales_count: product.sales_count,
            weight: product.weight,
            inner_code: product.inner_code,
            unit: product.unit,
            pack_unit: product.pack_unit,
            pack_quantity: product.pack_quantity
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        product.status = newStatus
      } catch (err) {
        console.error(`${action}失败:`, err)
        showMessage(`${action}失败`, { type: 'negative' })
      }
    },
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmAction。
    // 归 **B**：复制是**新增**，不改动、不删除任何既有数据（审计 §3.4 明确列为 B），故用主色键不用红。
    // 请求放进弹窗内：loading 防双击；失败留在弹窗里显示原因。
    async copyProduct (product) {
      await confirmAction({
        parent: this,
        title: '复制商品',
        message: `确定要复制商品 "${product.name}" 吗？`,
        consequence: '将新增一件以「(副本)」命名、库存为 0 且下架状态的商品，原商品不受影响。',
        okLabel: '复制',
        action: async () => {
          const token = localStorage.getItem('token')
          const copyData = {
            name: product.name + ' (副本)',
            description: product.description,
            price: product.price,
            original_price: product.original_price,
            // 2026-09-19 修正：此前误发 main_images 键（后端不识别）→ 副本丢图；POST 正确键为 images
            images: product.main_images || product.images,
            category: product.category,
            stock: 0,
            status: 'inactive',
            brand: product.brand,
            specification: product.specification,
            model: product.model,
            tier: product.tier,
            sales_count: 0,
            weight: product.weight,
            inner_code: product.inner_code,
            unit: product.unit,
            pack_unit: product.pack_unit,
            pack_quantity: product.pack_quantity
          }

          await this.axios.post(
            apiConfig.apiList.createProduct,
            copyData,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          showMessage('商品复制成功', { type: 'positive' })
          this.fetchProducts()
        }
      })
    },
    // 批量操作
    toggleSelectAll () {
      if (this.selectAll) {
        this.selectedProducts = this.products.map(p => p.id)
      } else {
        this.selectedProducts = []
      }
    },
    toggleBatchMode () {
      this.batchMode = !this.batchMode
      if (!this.batchMode) {
        this.selectedProducts = []
        this.selectAll = false
      }
    },
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmAction。
    // 归 **B**：批量上架/下架可逆（再点一次即可回退），审计 §3.4 明确列为 B。
    // 标题含动态动作词（上架/下架），但 semantic 恒为 primary，不受影响。
    async batchUpdateStatus (status) {
      if (this.selectedProducts.length === 0) return
      const action = status === 'active' ? '上架' : '下架'
      const count = this.selectedProducts.length
      await confirmAction({
        parent: this,
        title: `批量${action}商品`,
        message: `确定要批量${action} ${count} 个商品吗？`,
        consequence: `选中的 ${count} 个商品状态将被改为「${action === '上架' ? '在售' : '下架'}」，可再次批量改回。`,
        okLabel: `批量${action}`,
        action: async () => {
          const token = localStorage.getItem('token')
          for (const id of this.selectedProducts) {
            const product = this.products.find(p => p.id === id)
            if (!product) continue
            // 后端 PUT /products/:id 是全字段更新（name NOT NULL），必须发完整商品数据 + 目标 status
            await this.axios.put(
              `${apiConfig.apiList.updateProduct}/${id}`,
              { ...product, status },
              { headers: { Authorization: `Bearer ${token}` } }
            )
          }
          showMessage(`已批量${action} ${count} 个商品`, { type: 'positive' })
          this.selectedProducts = []
          this.selectAll = false
          this.fetchProducts()
        }
      })
    },
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmDanger。
    // 归 **C**：循环 DELETE /admin/products/:id（`DELETE FROM products`）＝**批量物理删除、不可恢复**
    // （审计 §3.3 C 类 (a)(c)，文案自述「此操作不可恢复」）。C 类恒 persistent，防误点遮罩把危险确认点掉。
    // 请求放进弹窗内：loading 防双击；失败留在弹窗里显示原因。
    async batchDelete () {
      if (this.selectedProducts.length === 0) return
      const count = this.selectedProducts.length
      await confirmDanger({
        parent: this,
        title: '批量删除商品',
        message: `确定要批量删除 ${count} 个商品吗？`,
        consequence: `选中的 ${count} 个商品记录将被永久删除且不可恢复；删除过程中若中途失败，已删除的商品不会回滚。`,
        okLabel: `删除 ${count} 个`,
        action: async () => {
          const token = localStorage.getItem('token')
          for (const id of this.selectedProducts) {
            await this.axios.delete(
              `${apiConfig.apiList.deleteProduct}/${id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
          }
          showMessage(`已删除 ${count} 个商品`, { type: 'positive' })
          this.selectedProducts = []
          this.selectAll = false
          this.fetchProducts()
        }
      })
    },
    // 获取标签列表
    async fetchTags () {
      try {
        const res = await this.axios.get('/admin/product-tags')
        if (res.data.code === 200) {
          const tags = res.data.data || []
          this.availableModels = tags.filter(t => t.tag_type === 'model')
          // 动态填充所有 spec_* 类型选项
          this.specOptions = {}
          tags.filter(t => t.tag_type.startsWith('spec_')).forEach(t => {
            if (!this.specOptions[t.tag_type]) this.specOptions[t.tag_type] = []
            this.specOptions[t.tag_type].push(t)
          })
        }
      } catch (err) {
        console.error('获取标签失败:', err)
      }
    },
    // 获取商品搜索词
    async fetchSearchTerms (productId) {
      try {
        const res = await this.axios.get(`/admin/product-search-terms?productId=${productId}`)
        if (res.data.code === 200) {
          this.form.search_terms = res.data.data || []
        }
      } catch (err) {
        console.error('获取搜索词失败:', err)
        this.form.search_terms = []
      }
    },
    // 新增搜索词
    async addSearchTerm () {
      const t = this.newSearchTerm
      // A 类（校验提示）：缺必填输入 → warning，不弹窗
      if (!t.term || !t.term.trim()) { showMessage('请输入搜索词', { type: 'warning' }); return }
      try {
        const res = await this.axios.post('/admin/product-search-terms', {
          product_id: this.editingProduct.id,
          term: t.term.trim(),
          term_type: t.term_type,
          weight: t.weight
        })
        if (res.data.code === 200) {
          this.form.search_terms.push({
            id: res.data.data.id,
            term: t.term.trim(),
            term_type: t.term_type,
            weight: t.weight,
            status: 'active'
          })
          this.newSearchTerm = { term: '', term_type: 'alias', weight: 5 }
        } else {
          showMessage(res.data.message || '添加失败', { type: 'negative' })
        }
      } catch (err) {
        console.error('添加搜索词失败:', err)
        showMessage('添加失败', { type: 'negative' })
      }
    },
    // 保存搜索词修改（term/term_type/weight/status）
    async saveSearchTerm (st) {
      try {
        const res = await this.axios.put(`/admin/product-search-terms/${st.id}`, {
          term: st.term,
          term_type: st.term_type,
          weight: st.weight,
          status: st.status
        })
        if (res.data.code === 200) {
          showMessage('搜索词已保存', { type: 'positive' })
        } else {
          showMessage(res.data.message || '保存失败', { type: 'negative' })
        }
      } catch (err) {
        console.error('保存搜索词失败:', err)
        showMessage('保存失败', { type: 'negative' })
      }
    },
    // 删除搜索词
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmDanger。
    // 归 **C**：DELETE /admin/product-search-terms/:id 是 `DELETE FROM product_search_term`（服务端 admin.js:3473）
    // ——**物理删除、不可恢复**（审计 §3.3 C 类 (a)）；搜索词删除会影响用户端搜索命中，故给红色确认。
    async removeSearchTerm (st, idx) {
      await confirmDanger({
        parent: this,
        title: '删除搜索词',
        message: `确定删除搜索词"${st.term}"？`,
        consequence: '该搜索词将被永久删除且不可恢复，用户端将不再通过它命中本商品。',
        okLabel: '删除',
        action: async () => {
          const res = await this.axios.delete(`/admin/product-search-terms/${st.id}`)
          if (!res.data || res.data.code !== 200) {
            throw new Error((res.data && res.data.message) || '删除失败')
          }
          this.form.search_terms.splice(idx, 1)
        }
      })
    },
    isModelSelected (modelValue) {
      return this.form.applicable_models && this.form.applicable_models.includes(modelValue)
    },
    toggleModel (modelValue) {
      if (!this.form.applicable_models) {
        this.form.applicable_models = []
      }
      const idx = this.form.applicable_models.indexOf(modelValue)
      if (idx > -1) {
        this.form.applicable_models.splice(idx, 1)
      } else {
        this.form.applicable_models.push(modelValue)
      }
    },
    updateSpecLabel () {
      const specs = this.form.product_specs
      const parts = Object.values(specs).filter(v => v !== '' && v !== null && v !== undefined)
      if (parts.length > 0) {
        this.form.specification = parts.join(' ')
      }
    },
    updateSearchTags () {
      this.form.search_tags = this.searchTagsInput.split(/[,，]/).map(s => s.trim()).filter(Boolean)
    },
    // CSV 导出
    exportCSV () {
      const headers = ['名称', '描述', '价格', '原价', '库存', '分类', '品牌', '型号', '规格', '档位', '内部编码', '最小单位', '包装单位', '换算数量', '排序权重', '销量', '维修难度', '管理员评分', '主图URLs', '详情图URLs']
      const rows = this.products.map(p => [
        p.name,
        p.description || '',
        p.price,
        p.original_price || '',
        p.stock,
        p.category,
        p.brand || '',
        p.model || '',
        p.specification || '',
        p.tier || '正',
        p.inner_code || '',
        p.unit || '个',
        p.pack_unit || '',
        p.pack_quantity || 1,
        p.weight || 100,
        p.sales_count || 0,
        p.repair_difficulty || 3,
        p.admin_override_rating || 0,
        Array.isArray(p.images) ? p.images.join(';') : (p.image || ''),
        Array.isArray(p.detail_images) ? p.detail_images.join(';') : ''
      ])

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n')

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `products_${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
    },
    // 下载模板
    downloadTemplate () {
      const headers = ['名称', '描述', '价格', '原价', '库存', '分类', '品牌', '型号', '规格', '档位', '内部编码', '最小单位', '包装单位', '换算数量', '排序权重', '销量', '维修难度', '管理员评分', '主图URLs', '详情图URLs']
      const example = ['米其林轮胎 PS4', '高性能运动轮胎', '458', '599', '120', 'tire', '米其林', 'PS4-1956515', '195/65R15 91V', '正', 'MICH-PS4-001', '条', '箱', '12', '100', '0', '3', '0', 'https://example.com/tire1.jpg;https://example.com/tire2.jpg', 'https://example.com/detail1.jpg;https://example.com/detail2.jpg']

      const csvContent = [headers, example]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n')

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'product_template.csv'
      link.click()
    },
    // 触发导入
    triggerImport () {
      this.$refs.fileInput.click()
    },
    // 处理文件导入
    handleFileImport (event) {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          this.parseCSV(e.target.result)
        } catch (err) {
          showMessage('CSV解析失败: ' + err.message, { type: 'negative' })
        }
      }
      reader.readAsText(file, 'UTF-8')
      event.target.value = '' // 重置input
    },
    // 解析CSV
    parseCSV (csvText) {
      const lines = csvText.trim().split('\n')
      // A 类（校验提示）：文件内容不足 → warning，不弹窗
      if (lines.length < 2) {
        showMessage('CSV文件至少需要包含表头和一行数据', { type: 'warning' })
        return
      }

      // 解析表头
      const headers = this.parseCSVLine(lines[0]).map(h => h.trim())
      const requiredFields = ['名称', '价格']
      const missingFields = requiredFields.filter(f => !headers.includes(f))
      if (missingFields.length) {
        showMessage(`缺少必需字段: ${missingFields.join(', ')}`, { type: 'warning' })
        return
      }

      // 解析数据行
      this.importData = []
      this.importErrors = []

      // 品类匹配源（品类数据化第一阶段）：API active 选项（slug+中文名双匹配）+
      // 内置字典全集兜底（停用品类仍可导入——存在即合法，只是不在选择器出现）
      const catOptions = getCategoriesForSelector()

      for (let i = 1; i < lines.length; i++) {
        const values = this.parseCSVLine(lines[i])
        const row = {}
        headers.forEach((h, idx) => {
          row[h] = values[idx] || ''
        })

        // 品类双匹配：先 slug 精确匹配，再中文名反查；未知/为空 → 行级报错，
        // 不再静默回退幽灵 slug 'other'（字典无此类，落库即脏数据）
        const rawCat = (row['分类'] || '').trim()
        let resolvedCat = ''
        if (rawCat) {
          const bySlug = catOptions.find(o => o.value === rawCat)
          if (bySlug) resolvedCat = bySlug.value
          else {
            const byName = catOptions.find(o => o.label === rawCat)
            if (byName) resolvedCat = byName.value
            else if (CATEGORY_DICTIONARY[rawCat]) resolvedCat = rawCat
          }
        }

        const item = {
          name: row['名称']?.trim(),
          description: row['描述']?.trim() || '',
          price: parseFloat(row['价格']),
          original_price: row['原价'] ? parseFloat(row['原价']) : null,
          stock: parseInt(row['库存']) || 0,
          category: resolvedCat,
          brand: row['品牌']?.trim() || '',
          model: row['型号']?.trim() || '',
          specification: row['规格']?.trim() || '',
          tier: row['档位']?.trim() || '正',
          inner_code: row['内部编码']?.trim() || '',
          unit: row['最小单位']?.trim() || '个',
          pack_unit: row['包装单位']?.trim() || '',
          pack_quantity: parseInt(row['换算数量']) || 1,
          weight: parseFloat(row['排序权重']) || 100,
          sales_count: parseInt(row['销量']) || 0,
          repair_difficulty: parseInt(row['维修难度']) || 3,
          admin_override_rating: parseInt(row['管理员评分']) || 0,
          images: row['主图URLs']?.trim() ? row['主图URLs'].split(';').map(url => url.trim()).filter(Boolean) : [],
          detail_images: row['详情图URLs']?.trim() ? row['详情图URLs'].split(';').map(url => url.trim()).filter(Boolean) : [],
          errors: []
        }

        // 验证
        if (!item.name) item.errors.push('名称不能为空')
        if (isNaN(item.price) || item.price < 0) item.errors.push('价格无效')
        if (!item.category) item.errors.push(rawCat ? `未知分类「${rawCat}」（支持 slug 或中文名）` : '分类不能为空')
        if (item.errors.length) this.importErrors.push({ row: i, errors: item.errors })

        this.importData.push(item)
      }

      this.showImportPreview = true
    },
    // 解析CSV行（处理引号）
    parseCSVLine (line) {
      const result = []
      let current = ''
      let inQuotes = false

      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        const nextChar = line[i + 1]

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"'
            i++ // 跳过下一个引号
          } else {
            inQuotes = !inQuotes
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }
      result.push(current.trim())
      return result
    },
    // 确认导入
    async confirmImport () {
      const validItems = this.importData.filter(item => !item.errors || item.errors.length === 0)
      if (!validItems.length) return

      const token = localStorage.getItem('token')
      let success = 0
      let failed = 0

      for (const item of validItems) {
        try {
          await this.axios.post(
            apiConfig.apiList.createProduct,
            item,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          success++
        } catch (err) {
          console.error('导入失败:', item.name, err)
          failed++
        }
      }

      // A 类（批量结果摘要）：全成功=positive；有失败（failed>0）升级为 warning（审计 §5.3）
      showMessage(`导入完成: ${success} 成功, ${failed} 失败`, { type: failed > 0 ? 'warning' : 'positive' })
      this.showImportPreview = false
      this.importData = []
      this.fetchProducts()
    }
  },
  created () {
    this.fetchProducts()
    this.fetchTags()
    // 品类字典 API 主源（active 品类+经营者排序）：到位后重建选择器选项
    fetchCategoryDictionary().then(() => this.syncCategoryOptions())
    // "惠/靓/正"角标全局开关（默认 true，显式 false 才隐藏）——列表预览角标与 C 端同一开关
    fetchPublicConfig().then(cfg => { this.showTierBadge = cfg.showHuiliangzhengBadge !== false })
  }
}
